import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import type { ProofOfServiceSnapshot } from "../data/proof-of-service";
import type { FrozenServiceWeekCoverageRow, FrozenServiceWeekWorkSegment, ServiceProvenance } from "../data/service-week-approval";
import { paginateProofRows, type ProofPage, type ProofSection } from "../lib/proof-of-service-pages";
import { serviceExceptionCopy } from "../lib/service-week";
import {
  ClientArtifactFooter, ClientArtifactHeader, ClientArtifactPage,
  clientArtifactDay as day, clientArtifactInstant as instant,
} from "./ClientArtifactPage";
import "../styles/proof-of-service.css";

export { CLIENT_ARTIFACT_INTEGRITY as PROOF_INTEGRITY_FOOTER } from "../data/client-artifact-contract";
const sections: ProofSection[] = ["coverage", "exceptions", "incidents"];
const headings: Record<ProofSection, string[]> = {
  coverage: ["Day / date", "Post / route", "Scheduled", "Worked", "Check-ins / flags"],
  exceptions: ["Day / date", "Post / route", "Exception noted"],
  incidents: ["Occurred", "Post / site", "Category", "Severity", "Review state"],
};
const sectionNames: Record<ProofSection, string> = { coverage: "Coverage by shift", exceptions: "Exceptions", incidents: "Incident summary" };
const provenance: Record<ServiceProvenance, string> = {
  device_verified: "✓ device-verified", officer_entered: "e officer-entered · no device proof",
  supervisor_set: "s supervisor-set · reason on file", missing: "- missing",
};
const category = { safety: "Safety", security: "Security", maintenance: "Maintenance", client_note: "Client note", other: "Other" };
const severity = { info: "Information", low: "Low", medium: "Medium", high: "High", critical: "Critical" };
const reviewState = { submitted: "Submitted", under_review: "Under review", locked: "Locked" };
type SheetRow =
  | { section: "coverage"; coverage: FrozenServiceWeekCoverageRow; work: FrozenServiceWeekWorkSegment | null; continuation: boolean }
  | { section: "exceptions"; exception: ProofOfServiceSnapshot["exceptions"][number] | null }
  | { section: "incidents"; incident: ProofOfServiceSnapshot["incidents"][number] | null };

function Summary({ summary }: { summary: ProofOfServiceSnapshot["summary"] }) {
  return <dl className="pos-paper-summary">
      <div><dt>Scheduled seats with recorded service</dt><dd>{summary.coveredSeatCount} of {summary.publishedSeatCount}</dd></div>
      <div><dt>Uncovered seats</dt><dd>{summary.uncoveredSeatCount}</dd></div>
      <div><dt>Check-ins device-verified</dt><dd>{summary.verifiedCheckInCount} of {summary.expectedCheckInCount}</dd></div>
      <div><dt>Missed check-ins</dt><dd>{summary.missedCheckInCount}</dd></div>
      <div><dt>Exceptions</dt><dd>{summary.totalExceptionCount}</dd></div>
      <div><dt>Incidents</dt><dd>{summary.incidentCount}</dd></div>
    </dl>;
}
const recordsNote = <p className="pos-paper-records-note">Full incident records and any separate timestamped SOS logs are available on request.</p>;
function WorkBoundary({ label, value, source, timeZone }: { label: string; value: string | null; source: ServiceProvenance; timeZone: string }) {
  return <div className="pos-paper-work"><span>{label}: {value === null ? "- missing" : instant(value, timeZone)}</span>{value !== null ? <span>{provenance[source]}</span> : null}</div>;
}
function Row({ row, timeZone }: { row: SheetRow; timeZone: string }) {
  if (row.section === "coverage") {
    const { coverage: item, work, continuation } = row;
    return <>
      <td>{day(item.serviceDay)}{continuation ? <span className="pos-paper-continuation">Same seat: continued work interval</span> : null}</td>
      <td>{item.serviceLabel}{item.templateLabel ? <span className="pos-paper-template"><i className={`pos-template-${item.templateStyleToken}`} aria-hidden="true" />{item.templateLabel}</span> : <span>! unscheduled</span>}</td>
      <td>{item.kind === "scheduled_seat" ? <>{instant(item.scheduledStart, timeZone)}<br />to {instant(item.scheduledEnd, timeZone)}</> : "! unscheduled"}</td>
      <td>{work === null ? <span>- missing work record</span> : <><WorkBoundary label="Start" value={work.recordedStart} source={work.startProvenance} timeZone={timeZone} /><WorkBoundary label="End" value={work.recordedEnd} source={work.endProvenance} timeZone={timeZone} /></>}</td>
      <td>{continuation ? <span>Same seat; counts above</span> : <><span>{item.verifiedCheckInCount} of {item.expectedCheckInCount} ✓ device-verified</span><span>{item.expectedCheckInCount - item.verifiedCheckInCount} - missing</span></>}
        {[...new Set([...item.exceptionCodes, ...(work?.exceptionCodes ?? [])])].map((code) => <span key={code}>! {serviceExceptionCopy(code)}</span>)}
      </td>
    </>;
  }
  if (row.section === "exceptions") {
    if (row.exception === null) return <td colSpan={3}>No exceptions were recorded in the complete approved week.</td>;
    const item = row.exception;
    return <><td>{day(item.serviceDay)}{item.occurredAt ? <span>{instant(item.occurredAt, timeZone)}</span> : null}</td><td>{item.serviceLabel}{item.checkpointLabel ? <span>{item.checkpointLabel}</span> : null}</td><td>! {item.sentence}</td></>;
  }
  if (row.incident === null) return <td colSpan={5}>No incidents were recorded in the complete approved week.</td>;
  const item = row.incident;
  return <><td>{instant(item.occurredAt, timeZone)}</td><td>{item.postLabel ?? "Site"}</td><td>{category[item.category]}</td><td>{severity[item.severity]}</td><td>{reviewState[item.reviewState]}</td></>;
}
function SectionColumns({ section }: { section: ProofSection }) {
  // Fixed-layout tables take column geometry from colgroup, not the second
  // header row after the full-width section title. Keep measurement identical.
  return <colgroup>{headings[section].map((heading) => <col key={heading} />)}</colgroup>;
}
function SectionHead({ section, continued = false }: { section: ProofSection; continued?: boolean }) {
  return <thead><tr><th className="pos-paper-section-title" colSpan={headings[section].length}>{sectionNames[section]}{continued ? " (continued)" : ""}</th></tr><tr>{headings[section].map((heading) => <th scope="col" key={heading}>{heading}</th>)}</tr></thead>;
}

/** This component receives only the validated printable object, never its control revision. */
export function ProofOfServiceSheet({ snapshot, onReadyChange }: { snapshot: ProofOfServiceSnapshot; onReadyChange: (ready: boolean) => void }) {
  const root = useRef<HTMLDivElement>(null);
  const readyCallback = useRef(onReadyChange);
  readyCallback.current = onReadyChange;
  const [layout, setLayout] = useState<{ snapshot: ProofOfServiceSnapshot; pages: ProofPage[] | null } | null>(null);
  const rows = useMemo<SheetRow[]>(() => {
    const result: SheetRow[] = [];
    const byCoverage = new Map<number, FrozenServiceWeekWorkSegment[]>();
    for (const segment of snapshot.workSegments) {
      const group = byCoverage.get(segment.coverageRowOrdinal) ?? [];
      group.push(segment);
      byCoverage.set(segment.coverageRowOrdinal, group);
    }
    for (const coverage of snapshot.coverage) {
      const segments = byCoverage.get(coverage.rowOrdinal) ?? [];
      if (segments.length === 0) result.push({ section: "coverage", coverage, work: null, continuation: false });
      else segments.forEach((work, index) => result.push({ section: "coverage", coverage, work, continuation: index > 0 }));
    }
    if (snapshot.exceptions.length === 0) result.push({ section: "exceptions", exception: null });
    else snapshot.exceptions.forEach((exception) => result.push({ section: "exceptions", exception }));
    if (snapshot.incidents.length === 0) result.push({ section: "incidents", incident: null });
    else snapshot.incidents.forEach((incident) => result.push({ section: "incidents", incident }));
    return result;
  }, [snapshot]);
  const pages = layout?.snapshot === snapshot ? layout.pages : null;

  useEffect(() => {
    let cancelled = false;
    readyCallback.current(false);
    const measure = () => {
      if (cancelled || !root.current) return;
      const measurement = root.current.querySelector<HTMLElement>(".pos-sheet-measure");
      if (!measurement) return;
      const headerHeight = measurement.querySelector(".pos-paper-header")?.getBoundingClientRect().height ?? 0;
      const footerHeight = measurement.querySelector(".pos-paper-footer")?.getBoundingClientRect().height ?? 0;
      const headSizes = {} as Record<ProofSection, number>;
      sections.forEach((section) => { headSizes[section] = measurement.querySelector(`[data-section="${section}"] thead`)?.getBoundingClientRect().height ?? 0; });
      const measured = Array.from(measurement.querySelectorAll<HTMLElement>("tbody tr"), (node, index) => ({ section: rows[index]!.section, height: Math.ceil(node.getBoundingClientRect().height) + 1 }));
      // 80px top/bottom paper padding, 24px gaps, 12px rounding/font safety reserve.
      const packed = headerHeight > 0 && footerHeight > 0 ? paginateProofRows(measured, headSizes, 1056 - 80 - 24 - 12 - headerHeight - footerHeight) : null;
      setLayout({ snapshot, pages: packed });
    };
    void (document.fonts?.ready ?? Promise.resolve()).then(measure);
    return () => { cancelled = true; readyCallback.current(false); };
  }, [snapshot, rows]);

  useLayoutEffect(() => {
    if (!pages || !root.current) return;
    const pageNodes = Array.from(root.current.querySelectorAll<HTMLElement>(".pos-paper:not(.pos-sheet-measure)"));
    const fits = pageNodes.length === pages.length && pageNodes.every((node) => {
      const content = node.querySelector(".pos-paper-content")?.getBoundingClientRect();
      const footer = node.querySelector(".pos-paper-footer")?.getBoundingClientRect();
      return content && footer && content.height > 0 && content.bottom <= footer.top - 8;
    });
    readyCallback.current(fits);
  }, [pages]);

  return <div className="pos-sheet" ref={root}>
    <div className="pos-paper pos-sheet-measure" aria-hidden="true">
      <ClientArtifactHeader title="Proof of service" header={snapshot.header} approvedAt={snapshot.receipt.approvedAt}><Summary summary={snapshot.summary} /></ClientArtifactHeader>
      {sections.map((section) => <table className={`pos-paper-table pos-paper-table--${section}`} data-section={section} key={section}><SectionColumns section={section} /><SectionHead section={section} /><tbody>{rows.map((row, index) => row.section === section ? <tr key={index}><Row row={row} timeZone={snapshot.header.timeZone} /></tr> : null)}</tbody></table>)}
      <ClientArtifactFooter header={snapshot.header} page={999} total={999}>{recordsNote}</ClientArtifactFooter>
    </div>
    {pages === null ? <p className="pos-sheet-layout-state" role="status">{layout?.snapshot === snapshot ? "Proof of service is unavailable right now. Check your access or try again." : "Preparing the printable pages..."}</p> : pages.map((page, pageIndex) => <ClientArtifactPage key={pageIndex} title="Proof of service" header={snapshot.header} approvedAt={snapshot.receipt.approvedAt}
      summary={<Summary summary={snapshot.summary} />} pagination={{ page: pageIndex + 1, total: pages.length }} recordsNote={recordsNote}>
        {page.groups.map((group, groupIndex) => <table className={`pos-paper-table pos-paper-table--${group.section}`} key={groupIndex}><SectionColumns section={group.section} /><SectionHead section={group.section} continued={group.continued} /><tbody>{group.rowIndexes.map((index) => <tr key={index}><Row row={rows[index]!} timeZone={snapshot.header.timeZone} /></tr>)}</tbody></table>)}
    </ClientArtifactPage>)}
  </div>;
}
